'use client';

import { c as _c } from "react/compiler-runtime";
import type { InboxPullRequest } from '@/app/(home)/inbox/types';
import type { InboxV2SectionResponse } from '@/data/pull-requests/use-inbox-v2-pull-requests';
import type { PrV2Label } from '@/lib/pr-v2/metadata-summary';
import type { PrV2LabelsResponse, PrV2SummaryResponse } from '@/lib/pr-v2/types';
import type { QueryKey } from '@tanstack/react-query';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { updatePrV2Labels } from './fetch-pr-v2';
import { prV2DiscussionQueryKey, prV2SummaryQueryKey } from './query-keys';
import { isSamePullRequest, patchInboxPullRequest } from '@/data/pull-requests/patch-inbox-pull-request';
import { clearPendingOptimisticMutation } from '@/data/pull-requests/pending-optimistic-mutations';
import { inboxV2MergedPullRequestsQueryKeyPrefix, inboxV2OpenPullRequestsQueryKeyPrefix } from '@/data/pull-requests/query-keys';
import { asRecord } from '@/lib/pr-v2/utils/as-record';

/** A label change. Added labels carry their color so the sidebar can show them before GitHub answers. */
export type PrV2LabelChanges = {
  add: PrV2Label[];
  remove: string[];
};
type MutationContext = {
  previousLabels: PrV2Label[];
  previousInboxQueries: Array<[QueryKey, InboxV2SectionResponse | undefined]>;
  optimisticUpdatedAt: string;
};
function applyLabelChanges(labels: PrV2Label[], changes: PrV2LabelChanges): PrV2Label[] {
  const removedNames = new Set(changes.remove.map(name => name.toLowerCase()));
  const nextLabels = labels.filter(label => !removedNames.has(label.name.toLowerCase()));
  const nextNames = new Set(nextLabels.map(label => label.name.toLowerCase()));
  for (const label of changes.add) {
    if (!nextNames.has(label.name.toLowerCase())) {
      nextLabels.push(label);
      nextNames.add(label.name.toLowerCase());
    }
  }
  return nextLabels;
}
function withLabels(current: PrV2SummaryResponse | undefined, labels: PrV2Label[]): PrV2SummaryResponse | undefined {
  if (!current) {
    return current;
  }
  const metadataRecord = asRecord(current.pullRequest.metadata) ?? {};
  const pullRequestMetadata = asRecord(metadataRecord.pullRequest) ?? {};
  return {
    ...current,
    pullRequest: {
      ...current.pullRequest,
      metadataSummary: {
        ...current.pullRequest.metadataSummary,
        labels
      },
      metadata: {
        ...metadataRecord,
        pullRequest: {
          ...pullRequestMetadata,
          labels
        }
      }
    }
  };
}

// Mirrors the inbox route, which builds its labels from the same stored name and color.
function toInboxLabels(labels: PrV2Label[]): InboxPullRequest['labels'] {
  return labels.map((label, index) => ({
    id: index,
    node_id: '',
    url: '',
    name: label.name,
    color: label.color ?? '',
    description: null,
    default: false
  }));
}

/**
 * Adds and removes pull request labels. The PR summary and inbox row show the change at once, a
 * failure restores both summary and inbox labels, and the refetch afterwards brings in what GitHub stored.
 */
export function usePrV2UpdateLabels(owner, repo, number) {
  const $ = _c(30);
  const queryClient = useQueryClient();
  let t0;
  if ($[0] !== number || $[1] !== owner || $[2] !== repo) {
    t0 = prV2SummaryQueryKey(owner, repo, number);
    $[0] = number;
    $[1] = owner;
    $[2] = repo;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  const summaryKey = t0;
  let t1;
  if ($[4] !== number || $[5] !== owner || $[6] !== repo) {
    t1 = prV2DiscussionQueryKey(owner, repo, number);
    $[4] = number;
    $[5] = owner;
    $[6] = repo;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  const discussionKey = t1;
  const t2 = Number(number);
  let t3;
  if ($[8] !== owner || $[9] !== repo || $[10] !== t2) {
    t3 = {
      owner,
      repo,
      number: t2
    };
    $[8] = owner;
    $[9] = repo;
    $[10] = t2;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  const pullRequest = t3;
  let t4;
  if ($[12] !== number || $[13] !== owner || $[14] !== repo) {
    t4 = changes => updatePrV2Labels(owner, repo, number, {
      add: changes.add.map(_temp),
      remove: changes.remove
    });
    $[12] = number;
    $[13] = owner;
    $[14] = repo;
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  let t5;
  let t6;
  if ($[16] !== pullRequest || $[17] !== queryClient || $[18] !== summaryKey) {
    t5 = async changes_0 => {
      await Promise.all([queryClient.cancelQueries({
        queryKey: summaryKey
      }), queryClient.cancelQueries({
        queryKey: inboxV2OpenPullRequestsQueryKeyPrefix()
      }), queryClient.cancelQueries({
        queryKey: inboxV2MergedPullRequestsQueryKeyPrefix()
      })]);
      const previousInboxQueries = [...queryClient.getQueriesData({
        queryKey: inboxV2OpenPullRequestsQueryKeyPrefix()
      }), ...queryClient.getQueriesData({
        queryKey: inboxV2MergedPullRequestsQueryKeyPrefix()
      })];
      const previousLabels = queryClient.getQueryData(summaryKey)?.pullRequest.metadataSummary.labels ?? [];
      const nextLabels = applyLabelChanges(previousLabels, changes_0);
      const optimisticUpdatedAt = new Date().toISOString();
      queryClient.setQueryData(summaryKey, current => withLabels(current, nextLabels));
      patchInboxPullRequest(queryClient, pullRequest, pr => ({
        ...pr,
        labels: toInboxLabels(nextLabels),
        updatedAt: optimisticUpdatedAt
      }));
      return {
        previousLabels,
        previousInboxQueries,
        optimisticUpdatedAt
      };
    };
    t6 = (error, _changes, context) => {
      toast.error(error.message || "Failed to update labels");
      clearPendingOptimisticMutation(pullRequest);
      if (!context) {
        return;
      }
      queryClient.setQueryData(summaryKey, current_0 => withLabels(current_0, context.previousLabels));
      for (const [queryKey, previous] of context.previousInboxQueries) {
        if (!previous) {
          continue;
        }
        queryClient.setQueryData(queryKey, current_1 => {
          if (!current_1) {
            return current_1;
          }
          const restoreLabels = (list, previousList) => {
            const previousPr = previousList.find(pr_0 => isSamePullRequest(pr_0, pullRequest));
            if (!previousPr) {
              return list;
            }
            return list.map(pr_1 => isSamePullRequest(pr_1, pullRequest) ? {
              ...pr_1,
              labels: previousPr.labels,
              updatedAt: pr_1.updatedAt === context.optimisticUpdatedAt ? previousPr.updatedAt : pr_1.updatedAt
            } : pr_1);
          };
          return {
            ...current_1,
            openPullRequests: restoreLabels(current_1.openPullRequests, previous.openPullRequests),
            mergedPullRequests: restoreLabels(current_1.mergedPullRequests, previous.mergedPullRequests)
          };
        });
      }
    };
    $[16] = pullRequest;
    $[17] = queryClient;
    $[18] = summaryKey;
    $[19] = t5;
    $[20] = t6;
  } else {
    t5 = $[19];
    t6 = $[20];
  }
  let t7;
  if ($[21] !== discussionKey || $[22] !== queryClient || $[23] !== summaryKey) {
    t7 = async () => {
      await Promise.all([queryClient.invalidateQueries({
        queryKey: summaryKey
      }), queryClient.invalidateQueries({
        queryKey: discussionKey
      }), queryClient.invalidateQueries({
        queryKey: inboxV2OpenPullRequestsQueryKeyPrefix()
      }), queryClient.invalidateQueries({
        queryKey: inboxV2MergedPullRequestsQueryKeyPrefix()
      })]);
    };
    $[21] = discussionKey;
    $[22] = queryClient;
    $[23] = summaryKey;
    $[24] = t7;
  } else {
    t7 = $[24];
  }
  let t8;
  if ($[25] !== t4 || $[26] !== t5 || $[27] !== t6 || $[28] !== t7) {
    t8 = {
      mutationFn: t4,
      onMutate: t5,
      onError: t6,
      onSettled: t7
    };
    $[25] = t4;
    $[26] = t5;
    $[27] = t6;
    $[28] = t7;
    $[29] = t8;
  } else {
    t8 = $[29];
  }
  return useMutation(t8);
}
function _temp(label) {
  return label.name;
}